import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import React from "react";
import { filtersAtom } from "../state/filter";
import { dashboardDataAtom } from "../state/dashboardData";
import { useAuth } from "./AuthContext";
import { API_BASE_URL } from "../config/api";

interface MemberDetails {
    securedstichRevenueShare: number;
    clientRevenueShare: number;
    companyName: string;
}

function updateUrlParams(filters: { productType: any; member: any; fromDate: any; toDate: any }) {
    const params = new URLSearchParams();
    params.set("productType", filters.productType);
    if (filters.member) params.set("member", filters.member.toString());
    params.set("fromDate", filters.fromDate.toISOString());
    params.set("toDate", filters.toDate.toISOString());
    window.history.replaceState({}, "", `?${params.toString()}`);
}

// Added [color-scheme:dark] to ensure the native calendar icon turns white
const INPUT =
    "w-full px-3 py-2 bg-[#1a1a1a] border border-[#2d2d2d] rounded text-sm text-gray-200 focus:outline-none focus:border-blue-600 transition-colors [color-scheme:dark]";

const LABEL = "block text-xs text-gray-500 font-medium uppercase tracking-wide mb-1";

export default function DashboardFilters() {
    const { user } = useAuth();
    const [filters, setFilters] = useRecoilState(filtersAtom);
    const [productOptions] = useState<string[]>([]);
    const [dashboardData, setDashboardData] = useRecoilState(dashboardDataAtom);
    const [loading, setLoading] = useState(false);
    const [memberDetails, setMemberDetails] = useState<MemberDetails | null>(null);
    const [members, setMembers] = useState<{ memberUuid: string; companyName: string }[]>([]);

    const isAdmin = user?.userName === "omkark@securedstitch.com";

    // Fetch all members list (admin only)
    useEffect(() => {
        if (!isAdmin) return;
        fetch(`${API_BASE_URL}/api/member`)
            .then((r) => r.json())
            .then((data) => setMembers(data))
            .catch(() => {});
    }, [isAdmin]);

    // Fetch member details (non-admin only)
    useEffect(() => {
        if (isAdmin) return;
        const memberUuid = localStorage.getItem("memberUuid");
        if (!memberUuid) return;

        fetch(`${API_BASE_URL}/api/crd/memberDetails`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ memberUuid }),
        })
            .then((res) => res.json())
            .then((data) => setMemberDetails(data))
            .catch((error) => console.error("Error fetching member details:", error));
    }, [isAdmin]);

    /**
     * Fetch dashboard data (non-admin only) — NO date filter applied here.
     * We store the FULL unfiltered dataset in dashboardDataAtom.
     */
    useEffect(() => {
        if (isAdmin) return;

        const memberUuid = localStorage.getItem("memberUuid");
        if (!memberUuid) return;

        setLoading(true);

        fetch(`${API_BASE_URL}/api/crd/sales`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ memberUuid }),
        })
            .then((res) => res.json())
            .then((data) => {
                if (!Array.isArray(data)) {
                    setDashboardData([]);
                    return;
                }
                setDashboardData(data);
            })
            .catch(() => setDashboardData([]))
            .finally(() => setLoading(false));
    }, [setDashboardData, isAdmin]);

    // Helpers to convert Date objects to YYYY-MM-DD for native HTML date inputs
    const toYMD = (d: Date) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
    };

    // Helper to convert YYYY-MM-DD string back to Date object
    const fromYMD = (str: string) => {
        const [y, m, d] = str.split("-");
        return new Date(Number(y), Number(m) - 1, Number(d));
    };

    return (
        <div className="bg-[#111111] border border-[#2d2d2d] rounded p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                
                {/* Product Type */}
                <div>
                    <label className={LABEL}>Product Type</label>
                    <select
                        value={filters.productType}
                        onChange={(e) => {
                            const updated = { ...filters, productType: e.target.value };
                            setFilters(updated);
                            updateUrlParams(updated);
                        }}
                        className={`${INPUT} cursor-pointer`}
                    >
                        <option value="All">All</option>
                        {productOptions.map((type) => (
                            <option key={type} value={type}>
                                {type}
                            </option>
                        ))}
                    </select>
                </div>

                {/* From Date */}
                <div>
                    <label className={LABEL}>From</label>
                    <input
                        type="date"
                        value={toYMD(filters.fromDate)}
                        onChange={(e) => {
                            if (e.target.value) {
                                const updated = { ...filters, fromDate: fromYMD(e.target.value) };
                                setFilters(updated);
                                updateUrlParams(updated);
                            }
                        }}
                        className={`${INPUT} cursor-pointer`}
                    />
                </div>

                {/* To Date */}
                <div>
                    <label className={LABEL}>To</label>
                    <input
                        type="date"
                        value={toYMD(filters.toDate)}
                        onChange={(e) => {
                            if (e.target.value) {
                                const updated = { ...filters, toDate: fromYMD(e.target.value) };
                                setFilters(updated);
                                updateUrlParams(updated);
                            }
                        }}
                        className={`${INPUT} cursor-pointer`}
                    />
                </div>

                {/* Member */}
                <div>
                    <label className={LABEL}>Member</label>
                    {isAdmin ? (
                        <select
                            className={`${INPUT} cursor-pointer`}
                            value={filters.member}
                            onChange={(e) => {
                                const updated = { ...filters, member: e.target.value };
                                setFilters(updated);
                                updateUrlParams(updated);
                            }}
                        >
                            <option value="">All Members</option>
                            {members.map((m) => (
                                <option key={m.memberUuid} value={m.memberUuid}>
                                    {m.companyName}
                                </option>
                            ))}
                        </select>
                    ) : (
                        <div className={`${INPUT} truncate cursor-default`}>
                            {memberDetails?.companyName || "Loading..."}
                        </div>
                    )}
                </div>
            </div>

            {/* Status Feedback */}
            {(loading || dashboardData.length === 0) && (
                <div className="mt-3 pt-3 border-t border-[#1f1f1f]">
                    {loading ? (
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 border-2 border-gray-600 border-t-gray-300 rounded-full animate-spin" />
                            <span className="text-xs text-gray-500">Loading data…</span>
                        </div>
                    ) : (
                        <p className="text-xs text-gray-600">No records found for the selected period.</p>
                    )}
                </div>
            )}
        </div>
    );
}